import { ConnectedCvEditionControl } from '../../cv/ConnectedCvEditionControl';
/**
 * Settings > Connected apps (web).
 *
 * List and detail live in one panel and swap in place, the way the settings
 * dialog's mobile breakpoint already moves between its category list and a
 * category: a route push would take the user out of the dialog they opened.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { UpdateConnectedAppInput, UserScope } from '@tabbio/types';
import { useToast } from '../../../hooks/useToast';
import {
  useConnectedApp,
  useConnectedAppCvOptions,
  useConnectedApps,
  useRevokeConnectedApp,
  useUpdateConnectedApp,
} from '../../../hooks/useConnectedApps';
import {
  consentCvSelectionId,
  isConnectedAppCvUnavailable,
  normalizeConsentCvId,
  scopeCopyKey,
  toggleScope,
} from '../../../lib/oauth-consent';
import { confirm } from '../../../lib/alert.web';
import { CvPickerSheet } from '../../cv/create/CvPickerSheet';
import {
  AppMark,
  EmptyState,
  HelpText,
  ListRow,
  PANEL,
  PanelBackButton,
  SectionTitle,
  ToggleRow,
  DestructiveButton,
  formatWebDate,
} from './ConnectedAppsParts.web';

export function ConnectedAppsPanel() {
  const [activeId, setActiveId] = useState<string | null>(null);

  return activeId ? (
    <ConnectedAppDetail
      connectionId={activeId}
      onBack={() => setActiveId(null)}
    />
  ) : (
    <ConnectedAppsList onOpen={setActiveId} />
  );
}

function ConnectedAppsList({ onOpen }: { onOpen: (id: string) => void }) {
  const { t, i18n } = useTranslation('common');
  const { data, isPending, error } = useConnectedApps();

  if (isPending) {
    return <HelpText>{t('settings.connectedApps.loading')}</HelpText>;
  }

  if (error) {
    return (
      <p style={{ fontSize: 13, fontWeight: 500, color: '#d93025' }}>
        {t('settings.connectedApps.loadFailed')}
      </p>
    );
  }

  const connections = data ?? [];

  return (
    <div style={PANEL}>
      <HelpText>{t('settings.connectedApps.subtitle')}</HelpText>

      {connections.length === 0 ? (
        <EmptyState
          title={t('settings.connectedApps.emptyTitle')}
          body={t('settings.connectedApps.emptyBody')}
        />
      ) : (
        <div>
          {connections.map((connection) => (
            <ListRow
              key={connection.id}
              title={connection.app.name}
              subtitle={
                // A deleted shared CV needs the user, so it outranks the date.
                isConnectedAppCvUnavailable(connection.cv)
                  ? t('settings.connectedApps.cvUnavailableShort')
                  : t('settings.connectedApps.connectedOn', {
                      date: formatWebDate(connection.createdAt, i18n.language),
                    })
              }
              leading={
                <AppMark
                  name={connection.app.name}
                  logoUrl={connection.app.logoUrl}
                />
              }
              onPress={() => onOpen(connection.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ConnectedAppDetail({
  connectionId,
  onBack,
}: {
  connectionId: string;
  onBack: () => void;
}) {
  const { t, i18n } = useTranslation('common');
  const toast = useToast();
  const { connection, isPending } = useConnectedApp(connectionId);
  const update = useUpdateConnectedApp(connectionId);
  const revoke = useRevokeConnectedApp();
  const [pickerOpen, setPickerOpen] = useState(false);
  const cvOptions = useConnectedAppCvOptions(Boolean(connection));

  if (isPending) {
    return <HelpText>{t('settings.connectedApps.loading')}</HelpText>;
  }

  if (!connection) {
    return (
      <div style={PANEL}>
        <PanelBackButton label={t('actions.back')} onPress={onBack} />
        <HelpText>{t('settings.connectedApps.notFound')}</HelpText>
      </div>
    );
  }

  const granted = connection.grantedScopes;
  const available = connection.availableScopes ?? granted;
  const shareCv = granted.includes('cv:read');
  const shareContact = granted.includes('cv:contact');
  // The CV this app was given was deleted: the app is refused until the user
  // chooses again, so the row says so instead of naming a CV (audit F09).
  const cvUnavailable = isConnectedAppCvUnavailable(connection.cv);

  const save = (input: UpdateConnectedAppInput) => {
    update.mutate(input, {
      onSuccess: () => toast.success(t('settings.connectedApps.updated')),
      onError: (error) =>
        toast.error(
          t('settings.connectedApps.updateFailed'),
          error instanceof Error ? error.message : undefined,
        ),
    });
  };

  const flipScope = (scope: UserScope) =>
    save({ grantedScopes: toggleScope(granted, scope, available) });

  const disconnect = async () => {
    const confirmed = await confirm({
      title: t('settings.connectedApps.disconnectTitle', {
        app: connection.app.name,
      }),
      message: t('settings.connectedApps.disconnectMessage', {
        app: connection.app.name,
      }),
      confirmLabel: t('settings.connectedApps.disconnect'),
      cancelLabel: t('actions.cancel'),
      destructive: true,
    });
    if (!confirmed) return;

    revoke.mutate(connection.id, {
      onSuccess: () => {
        toast.success(t('settings.connectedApps.disconnected'));
        onBack();
      },
      onError: (error) =>
        toast.error(
          t('settings.connectedApps.disconnectFailed'),
          error instanceof Error ? error.message : undefined,
        ),
    });
  };

  return (
    <div style={PANEL}>
      <PanelBackButton label={t('actions.back')} onPress={onBack} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <AppMark
          name={connection.app.name}
          logoUrl={connection.app.logoUrl}
          size={44}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{ fontSize: 18, fontWeight: 500, color: 'var(--text-primary)' }}
            dir="auto"
          >
            {connection.app.name}
          </div>
          {connection.app.homepageUrl ? (
            <a
              href={connection.app.homepageUrl}
              target="_blank"
              rel="noreferrer noopener"
              style={{
                fontSize: 12.5,
                color: 'var(--text-secondary)',
                wordBreak: 'break-all',
              }}
            >
              {connection.app.homepageUrl}
            </a>
          ) : null}
        </div>
      </div>

      <HelpText>
        {t('settings.connectedApps.connectedOn', {
          date: formatWebDate(connection.createdAt, i18n.language),
        })}
        {' · '}
        {connection.lastUsedAt
          ? t('settings.connectedApps.lastUsed', {
              date: formatWebDate(connection.lastUsedAt, i18n.language),
            })
          : t('settings.connectedApps.neverUsed')}
      </HelpText>

      <SectionTitle>{t('settings.connectedApps.accessTitle')}</SectionTitle>

      <div>
        {/* Photo only, not the profile scope: same copy as the consent screen. */}
        <ToggleRow
          title={t('oauth.consent.includePhoto')}
          body={t('settings.connectedApps.photoDescription')}
          checked={connection.shareAvatar}
          onChange={(shareAvatar) => save({ shareAvatar })}
        />

        {available.includes('cv:read') ? (
          <ToggleRow
            title={t(`oauth.scopes.${scopeCopyKey('cv:read')}.title`)}
            body={t(`oauth.scopes.${scopeCopyKey('cv:read')}.description`)}
            checked={shareCv}
            onChange={() => flipScope('cv:read')}
          />
        ) : null}

        {shareCv ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              paddingBlock: 12,
              borderBlockStart: '1px solid var(--input-border)',
            }}
          >
            <span style={{ flex: 1, minWidth: 0 }}>
              <span
                style={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 500,
                  color: 'var(--text-primary)',
                }}
              >
                {t('settings.connectedApps.cvRow')}
              </span>
              {cvUnavailable ? (
                <span
                  style={{
                    display: 'block',
                    fontSize: 12.5,
                    color: 'var(--tabbio-danger, #d93025)',
                    marginBlockStart: 2,
                  }}
                >
                  {t('settings.connectedApps.cvUnavailable')}
                </span>
              ) : (
                <span
                  style={{
                    display: 'block',
                    fontSize: 12.5,
                    color: 'var(--text-secondary)',
                    marginBlockStart: 2,
                  }}
                  dir="auto"
                >
                  {connection.cv?.title ?? t('settings.connectedApps.cvNone')}
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              style={{
                border: 0,
                background: 'transparent',
                color: 'var(--text-primary)',
                fontSize: 13,
                fontWeight: 500,
                fontFamily: 'inherit',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              {cvUnavailable
                ? t('settings.connectedApps.chooseCv')
                : t('oauth.consent.change')}
            </button>
          </div>
        ) : null}

        {shareCv ? <ConnectedCvEditionControl target={connection.cv?.id ?? cvOptions.options.find((cv) => cv.isMainCv)?.id ?? 'master'} current={connection.cv} pending={update.isPending} onSave={save} /> : null}

        {available.includes('cv:contact') ? (
          <ToggleRow
            title={t(`oauth.scopes.${scopeCopyKey('cv:contact')}.title`)}
            body={t(`oauth.scopes.${scopeCopyKey('cv:contact')}.description`)}
            checked={shareContact}
            disabled={!shareCv}
            onChange={() => flipScope('cv:contact')}
          />
        ) : null}
      </div>

      <div style={{ marginBlockStart: 18 }}>
        <DestructiveButton
          label={t('settings.connectedApps.disconnectApp', {
            app: connection.app.name,
          })}
          onPress={() => void disconnect()}
          disabled={revoke.isPending}
        />
      </div>

      <CvPickerSheet
        visible={pickerOpen}
        onClose={() => setPickerOpen(false)}
        cvs={cvOptions.options}
        loading={cvOptions.isLoading}
        selectedCvId={consentCvSelectionId(connection.cv?.id ?? null)}
        onSelect={(id) => save({ cvId: normalizeConsentCvId(id) })}
      />
    </div>
  );
}
